import { inject } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
} from '@ngrx/signals';
import { computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthApiService } from '../services/auth-api.service';
import { AuthUserDTO } from '@sandbox-platform/shared-ui';
import {
  LoginDTO,
  RegisterDTO,
  AuthResponseDTO,
  ChangePasswordDTO,
  CompanyContextDTO,
  LoginFlowResponseDTO,
  TokenResponseDTO,
} from '../types/auth.types';
import {
  initialState,
  LoginResult,
  STORAGE_KEYS,
  computeMustReset,
  enrichUserWithClaims,
  persistTokens,
  persistUser,
  persistSession,
  clearPersistedAuth,
  isCompanySelectionResponse,
  persistCompanyCookie,
  readCompanyCookie,
  decodeJWT,
} from './definitions';

export const AuthStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    isLoggedIn: computed(() => {
      return !!store.user() && !!store.tokens().access_token;
    }),
    userFullName: computed(() => {
      const user = store.user();
      return user ? `${user.first_name} ${user.last_name}` : '';
    }),
    isTokenExpired: computed(() => {
      const expiresIn = store.tokens().expires_in;
      if (!expiresIn) return true;
      return Date.now() >= expiresIn * 1000;
    }),
    // hasAdminOrSupport: computed(() => {
    //   const user = store.user();

    //   return RoleUtils.hasAdminOrSupport(user);
    // }),
    requiresPasswordReset: computed(() => {
      const stateFlag = store.mustResetPassword();
      const userFlag = store.user()?.must_reset_password;
      return !!(stateFlag || userFlag);
    }),
  })),

  withMethods((store) => {
    const authApiService = inject(AuthApiService);

    const updateCompanyContext = (
      company: CompanyContextDTO | null,
      options: { persistNull?: boolean } = {}
    ) => {
      if (company) {
        patchState(store, { currentCompany: company });
        localStorage.setItem(
          STORAGE_KEYS.currentCompany,
          JSON.stringify(company)
        );
        persistCompanyCookie(company);
        return;
      }

      if (options.persistNull) {
        patchState(store, { currentCompany: null });
        localStorage.removeItem(STORAGE_KEYS.currentCompany);
      }

      persistCompanyCookie(null);
    };

    const resetAuthState = () => {
      patchState(store, initialState);
      clearPersistedAuth();
    };

    /**
     * Apply auth response to store
     * @param authResponse - AuthResponseDTO
     */
    const applyAuthResponse = (authResponse: AuthResponseDTO) => {
      const jwtPayload = decodeJWT(authResponse.access_token);
      const mustReset = computeMustReset(
        authResponse.must_reset_password,
        authResponse.user?.must_reset_password
      );

      const userWithClaims = enrichUserWithClaims(
        authResponse.user,
        mustReset,
        jwtPayload
      );

      const tokens = {
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        expires_in: authResponse.expires_in,
      };

      patchState(store, {
        user: userWithClaims,
        isAuthenticated: true,
        tokens,
        sessionId: authResponse.session_id ?? null,
        mustResetPassword: mustReset,
        error: null,
      });

      persistTokens(tokens);
      persistUser(userWithClaims);
      persistSession(authResponse.session_id ?? null);
      updateCompanyContext(authResponse.current_company ?? null, {
        persistNull: true,
      });
    };

    /**
     * Apply token response to store
     * @param tokenResponse - TokenResponseDTO
     * @returns boolean
     */
    const applyTokenResponse = (tokenResponse: TokenResponseDTO): boolean => {
      const currentUser = store.user();

      if (!currentUser) {
        resetAuthState();
        return false;
      }

      const jwtPayload = decodeJWT(tokenResponse.access_token);
      const mustReset = computeMustReset(
        tokenResponse.must_reset_password,
        currentUser.must_reset_password
      );

      const updatedUser = enrichUserWithClaims(
        currentUser,
        mustReset,
        jwtPayload
      );

      const tokens = {
        access_token: tokenResponse.access_token,
        refresh_token: tokenResponse.refresh_token,
        expires_in: tokenResponse.expires_in,
      };

      const sessionId = tokenResponse.session_id ?? store.sessionId();

      patchState(store, {
        user: updatedUser,
        isAuthenticated: true,
        tokens,
        sessionId,
        mustResetPassword: mustReset,
        error: null,
      });

      persistTokens(tokens);
      persistUser(updatedUser);
      persistSession(sessionId);

      updateCompanyContext(tokenResponse.current_company ?? null, {
        persistNull: true,
      });

      return true;
    };

    const validateRestoredSession = async (refreshToken: string) => {
      try {
        const response = await firstValueFrom(
          authApiService.refreshToken({ refresh_token: refreshToken })
        );

        if (!response?.data || !applyTokenResponse(response.data)) {
          resetAuthState();
        }
      } catch {
        resetAuthState();
      }
    };

    const clearError = () => {
      patchState(store, { error: null });
    };

    const loadFromStorage = () => {
      const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
      const expiresIn = localStorage.getItem(STORAGE_KEYS.expiresIn);
      const userJson = localStorage.getItem(STORAGE_KEYS.user);
      const sessionId = localStorage.getItem(STORAGE_KEYS.sessionId);

      if (accessToken && refreshToken && expiresIn && userJson) {
        try {
          const user: AuthUserDTO = JSON.parse(userJson);
          const jwtPayload = decodeJWT(accessToken);
          const mustReset = !!user?.must_reset_password;
          const userWithClaims = enrichUserWithClaims(
            user,
            mustReset,
            jwtPayload
          );

          patchState(store, {
            user: userWithClaims,
            isAuthenticated: true,
            tokens: {
              access_token: accessToken,
              refresh_token: refreshToken,
              expires_in: parseInt(expiresIn, 10),
            },
            sessionId,
            mustResetPassword: mustReset,
          });
          persistUser(userWithClaims);
          void validateRestoredSession(refreshToken);
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          resetAuthState();
        }
      }
    };

    const loadCompanyContext = () => {
      const storedCompanyJson = localStorage.getItem(
        STORAGE_KEYS.currentCompany
      );

      if (storedCompanyJson) {
        try {
          const storedCompany = JSON.parse(
            storedCompanyJson
          ) as CompanyContextDTO;
          updateCompanyContext(storedCompany);
          return;
        } catch (error) {
          console.error('Error parsing current company:', error);
          localStorage.removeItem(STORAGE_KEYS.currentCompany);
          persistCompanyCookie(null);
        }
      }

      if (!store.currentCompany()) {
        const companyFromCookie = readCompanyCookie();
        if (companyFromCookie) {
          updateCompanyContext(companyFromCookie);
        }
      }
    };

    return {
      clearError,
      setAuthData: applyAuthResponse,
      clearAuthData: () => {
        resetAuthState();
      },
      loadFromStorage,

      /**
       * Login user
       * @param dto - LoginDTO
       * @returns Promise<LoginResult>
       */
      login: async (dto: LoginDTO): Promise<LoginResult> => {
        patchState(store, { isLoading: true, error: null });

        try {
          const response = await firstValueFrom(authApiService.login(dto));

          const payload = response?.data as LoginFlowResponseDTO | undefined;

          if (!payload) {
            throw new Error('Unexpected login response');
          }

          if (isCompanySelectionResponse(payload)) {
            patchState(store, { isLoading: false });
            return {
              status: 'company-selection-required',
              companies: payload.companies,
            };
          }

          applyAuthResponse(payload as AuthResponseDTO);

          patchState(store, { isLoading: false });
          return { status: 'success' };
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Login failed';
          patchState(store, { error: message, isLoading: false });
          throw error;
        }
      },

      /**
       * Change user password
       * @param dto - ChangePasswordDTO
       * @returns Promise<boolean>
       */
      changePassword: async (dto: ChangePasswordDTO): Promise<boolean> => {
        patchState(store, { isLoading: true, error: null });

        try {
          await firstValueFrom(authApiService.changePassword(dto));

          const currentUser = store.user();
          const updatedUser = currentUser
            ? { ...currentUser, must_reset_password: false }
            : null;

          patchState(store, {
            mustResetPassword: false,
            user: updatedUser ?? null,
          });

          persistUser(updatedUser);

          return true;
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to change password';
          patchState(store, { error: message });
          return false;
        } finally {
          patchState(store, { isLoading: false });
        }
      },

      /**
       * Register new user
       * @param dto - RegisterDTO
       * @returns Promise<boolean>
       */
      register: async (dto: RegisterDTO): Promise<boolean> => {
        patchState(store, { isLoading: true, error: null });

        try {
          const response = await firstValueFrom(authApiService.register(dto));
          if (response) {
            return true;
          }
          return false;
        } catch (error) {
          patchState(store, {
            error:
              error instanceof Error ? error.message : 'Registration failed',
          });
          return false;
        } finally {
          patchState(store, { isLoading: false });
        }
      },

      /**
       * Logout user
       * @returns Promise<void>
       */
      logout: async (): Promise<void> => {
        patchState(store, { isLoading: true });

        try {
          const refreshToken = store.tokens().refresh_token;
          const sessionId = store.sessionId();

          if (refreshToken) {
            await firstValueFrom(
              authApiService.logout({
                refresh_token: refreshToken,
                session_id: sessionId || undefined,
              })
            );
          }
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          resetAuthState();
          patchState(store, { isLoading: false });
        }
      },

      /**
       * Refresh user token
       * @returns Promise<boolean>
       */
      refreshToken: async (): Promise<boolean> => {
        const refreshToken = store.tokens().refresh_token;

        if (!refreshToken) {
          resetAuthState();
          return false;
        }

        try {
          const response = await firstValueFrom(
            authApiService.refreshToken({ refresh_token: refreshToken })
          );

          if (response?.data) {
            return applyTokenResponse(response.data);
          }

          return false;
        } catch (error) {
          resetAuthState();
          return false;
        }
      },

      /**
       * Update user
       * @param user - AuthUserDTO
       */
      updateUser: (user: AuthUserDTO) => {
        patchState(store, { user });
        persistUser(user);
      },

      /**
       * Load company context
       */
      loadCompanyContext,
    };
  })
);

// Create a type for the store instance
export type AuthStoreType = InstanceType<typeof AuthStore>;
